import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, Trophy, Calendar, Users, ChevronRight, Filter,
  ArrowUpRight, Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchHistory = () => {
  const { club, filters } = useOutletContext();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (club?.id) fetchReports();
  }, [club, filters]);

  const fetchReports = async () => {
    const token = localStorage.getItem('club_token');
    const params = new URLSearchParams();
    if (filters?.team_id) params.append('team_id', filters.team_id);
    const qs = params.toString();
    try {
      const res = await axios.get(`${API}/clubs/matches/history${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="match-history">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl uppercase tracking-wide">
            Match <span className="text-primary">Reports</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">{reports.length} match report(s)</p>
        </div>
        <Link to={`/club/${club?.id}/match-report`}
          className="bg-primary text-black hover:bg-primary/90 px-4 py-2 rounded-sm text-sm font-medium flex items-center gap-2"
          data-testid="new-report-btn"
        >
          <FileText size={16} /> New Report
        </Link>
      </div>

      {reports.length === 0 ? (
        <div className="bg-card border border-border/40 rounded-sm p-12 text-center">
          <Trophy size={36} className="mx-auto text-zinc-700 mb-3" />
          <p className="text-muted-foreground mb-2">No match reports found</p>
          <Link to={`/club/${club?.id}/match-report`} className="text-primary text-sm hover:underline">
            Submit your first match report
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map((r, i) => (
            <motion.div key={r.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
            >
              <Link to={`/club/${club?.id}/match-history/${r.id}`}
                className="flex items-center gap-4 p-4 bg-card border border-border/40 rounded-sm hover:border-primary/20 transition-colors group"
                data-testid={`report-${r.id}`}
              >
                {/* Result badge */}
                <div className={`w-12 h-12 rounded-sm flex flex-col items-center justify-center border shrink-0 ${
                  r.result === 'win' ? 'bg-emerald-500/10 border-emerald-500/20' :
                  r.result === 'loss' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-amber-500/10 border-amber-500/20'
                }`}>
                  <span className="font-heading text-lg leading-none">{r.our_score}-{r.opponent_score}</span>
                  <span className={`text-[8px] uppercase tracking-wider mt-0.5 ${
                    r.result === 'win' ? 'text-emerald-400' : r.result === 'loss' ? 'text-red-400' : 'text-amber-400'
                  }`}>{r.result}</span>
                </div>

                {/* Match info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium group-hover:text-primary transition-colors">{r.team_name}</p>
                    <span className="text-muted-foreground text-xs">vs</span>
                    <p className="text-sm text-muted-foreground truncate">{r.opponent}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar size={10} /> {formatDate(r.match_date)}</span>
                    <span className="flex items-center gap-1"><Users size={10} /> {r.player_count} players</span>
                    {r.has_summary && <span className="text-primary">AI Summary</span>}
                  </div>
                </div>

                {/* Avg score */}
                {r.avg_player_score > 0 && (
                  <div className="text-right shrink-0">
                    <p className={`font-heading text-lg ${r.avg_player_score >= 8 ? 'text-emerald-400' : r.avg_player_score >= 6 ? 'text-amber-400' : 'text-red-400'}`}>
                      {r.avg_player_score.toFixed(1)}
                    </p>
                    <p className="text-[9px] text-muted-foreground uppercase">avg score</p>
                  </div>
                )}

                <ChevronRight size={16} className="text-zinc-600 group-hover:text-primary shrink-0 transition-colors" />
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchHistory;
